import { IconButton, Stack } from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatStrikethrough,
  FormatListBulleted,
  FormatListNumbered,
  FormatClear,
} from "@mui/icons-material";

import type { Editor } from "@tiptap/react";

interface RichTextToolbarProps {
  editor: Editor | null;
}

const ToolbarButton = ({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}): React.JSX.Element => (
  <IconButton
    size="small"
    onClick={onClick}
    aria-label={label}
    sx={{
      color: active ? "brandPrimary.main" : "text.secondary",
      borderRadius: 1,
    }}
  >
    {icon}
  </IconButton>
);

const RichTextToolbar = ({
  editor,
}: RichTextToolbarProps): React.JSX.Element | null => {
  if (!editor) return null;

  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{
        px: 2,
        py: 0.5,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <ToolbarButton
        active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
        icon={<FormatBold fontSize="small" />}
        label="Bold"
      />
      <ToolbarButton
        active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        icon={<FormatItalic fontSize="small" />}
        label="Italic"
      />
      <ToolbarButton
        active={editor.isActive("underline")}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        icon={<FormatUnderlined fontSize="small" />}
        label="Underline"
      />
      <ToolbarButton
        active={editor.isActive("strike")}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        icon={<FormatStrikethrough fontSize="small" />}
        label="Strikethrough"
      />
      <ToolbarButton
        active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        icon={<FormatListBulleted fontSize="small" />}
        label="Bullet list"
      />
      <ToolbarButton
        active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        icon={<FormatListNumbered fontSize="small" />}
        label="Ordered list"
      />
      <ToolbarButton
        active={false}
        onClick={() =>
          editor.chain().focus().clearNodes().unsetAllMarks().run()
        }
        icon={<FormatClear fontSize="small" />}
        label="Clear formatting"
      />
    </Stack>
  );
};

export default RichTextToolbar;
